import { Icon } from '../../../components/icons/Icon'
import { StatusPill } from '../../../components/StatusPill'
import { lifecycleStatusPill } from '../cartridge-studio/lifecycleStatus'
import type { OverlayStatus } from '../../../types/eligibilityOverlay'

export interface OverlayApproveBarProps {
  status: OverlayStatus
  approvedBy: string | null
  approvedAt: string | null
  busy: boolean
  /** Set once a SELF-APPROVAL 403 is caught (AC3's failure path) — cleared on any new attempt.
   * Never set for a capability-denied 403 (a different cause, surfaced separately by the caller
   * via `approveError`, not mislabeled as "you authored this overlay"). */
  heldReason: string | null
  onApprove: () => void
  /** AC1/AC3/AC7 — 'loading' suppresses the whole action row (no flash of a button/reason before
   * the capability check resolves); 'ready' means `canApprove`/`capabilityError` below reflect the
   * real `GET /me/ui-config` `eligibility.overlay-approve` component key. */
  capabilityStatus: 'loading' | 'ready'
  /** Whether the signed-in actor holds `eligibility_overlay:A` at all (server-derived, never a
   * client-side guess). */
  canApprove: boolean
  /** Set only when the capability check itself failed (network/5xx) — fail-closed like `!canApprove`
   * for rendering, but surfaced with its own honest reason. */
  capabilityError: string | null
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

/**
 * AC3 — the maker≠checker approve control. Mirrors `MakerCheckerBar`'s "disabled-with-reason" idiom
 * (design-system.md §4.9 — `aria-disabled`, stays focusable, `role="status"` reason row), but is a
 * SINGLE-action bar (`draft/review → approved`, IB-288 §5.2 has one `approve()` call, not
 * `MakerCheckerBar`'s 4-step lifecycle) — the pattern reference, not a direct reuse (design doc §3.2).
 *
 * <p><b>Two layers of "disabled," now both real (IB-289 Verifier fix):</b> (1) missing
 * `eligibility_overlay:A` entirely — pre-click disabled-with-reason, since `useOverlayCapabilities`
 * now resolves this client-side from the real `SecurityConfigService.CAPABILITY_UI_COMPONENTS`
 * binding; (2) self-authorship — STILL cannot be pre-disabled (no `/me`-actor-id endpoint exists to
 * compare against `createdBy`, and the merged `TenantEligibilityOverlayView` carries no
 * `canApprove`/`approveBlockedReason` hint — the design doc's §2.4 PROPOSED ripple was not taken
 * up), so THIS one case still renders Approve enabled and relies on the real, server-enforced 403
 * (`EligibilityOverlaySelfApprovalException`), surfaced inline verbatim as the honest "held" state
 * once clicked. Filed as an IB-288 follow-up to close this remaining gap.
 */
export function OverlayApproveBar({
  status,
  approvedBy,
  approvedAt,
  busy,
  heldReason,
  onApprove,
  capabilityStatus,
  canApprove,
  capabilityError,
}: OverlayApproveBarProps) {
  const pill = lifecycleStatusPill(status)
  const canAttemptApprove = status === 'draft' || status === 'review'

  return (
    <div role="group" aria-label="Overlay approval" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)' }}>
        <Icon name="check-circle" size={16} />
        <StatusPill {...pill} />
        {(status === 'approved' || status === 'published') && approvedBy ? (
          <span style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
            Approved by {approvedBy}
            {approvedAt ? ` on ${formatDate(approvedAt)}` : ''}
          </span>
        ) : null}
      </div>

      {/* AC1/AC7 — no flash: render nothing for the action row until the capability check resolves. */}
      {canAttemptApprove && capabilityStatus === 'ready' ? (
        <>
          <div>
            <button
              type="button"
              className="iw-btn iw-btn--primary"
              aria-disabled={!canApprove || undefined}
              disabled={busy}
              onClick={canApprove ? onApprove : undefined}
              aria-describedby={!canApprove ? 'iw-overlay-approve-capability-reason' : undefined}
            >
              {busy ? 'Approving…' : 'Approve'}
            </button>
          </div>

          {!canApprove ? (
            <div
              role="status"
              id="iw-overlay-approve-capability-reason"
              style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--iw-space-2)', color: 'var(--iw-text-muted)' }}
            >
              <Icon name="alert-circle" size={13} />
              <div>
                {capabilityError
                  ? `Couldn't confirm your access to approve overlays (${capabilityError}).`
                  : 'You need the approver capability (eligibility_overlay:A) to approve this overlay.'}
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      {heldReason ? (
        <div role="status" style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--iw-space-2)', color: 'var(--iw-warning)' }}>
          <Icon name="alert-circle" size={14} />
          <div>
            <div>Held — awaiting a second qualified reviewer (maker ≠ checker); you authored this overlay.</div>
            <div style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>{heldReason}</div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
